'use client'

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import {
  AdjustmentsHorizontalIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { SearchFacet } from '@/search/types'
import { useRouter, useSearchParams } from 'next/navigation'
import { FILTER_OPTIONS } from '../constants'

const FILTER_PREFIX = 'filter:'
const PRODUCT_TYPE_PREFIX = 'productType:'

export const FilterSelectorClient = ({
  facets,
  initialFilters,
  initialProductTypes,
  isLoading,
  query,
}: FilterSelectorClientProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const productTypeOptions = getProductTypeOptions(facets, initialProductTypes)
  const selectedValues = [
    ...initialFilters.map(filter => `${FILTER_PREFIX}${filter}`),
    ...initialProductTypes.map(productType => `${PRODUCT_TYPE_PREFIX}${productType}`),
  ]
  const activeFilterCount = initialFilters.length + initialProductTypes.length

  if (!query && activeFilterCount === 0) return null

  const updateFilters = (newSelectedValues: string[]) => {
    const params = new URLSearchParams(searchParams.toString())
    const filters = newSelectedValues
      .filter(value => value.startsWith(FILTER_PREFIX))
      .map(value => value.slice(FILTER_PREFIX.length))
      .filter(filter => FILTER_OPTIONS.some(option => option.id === filter))
    const productTypes = newSelectedValues
      .filter(value => value.startsWith(PRODUCT_TYPE_PREFIX))
      .map(value => value.slice(PRODUCT_TYPE_PREFIX.length))
      .filter(Boolean)

    if (filters.length > 0) {
      params.set('filter', Array.from(new Set(filters)).join(','))
    } else {
      params.delete('filter')
    }

    if (productTypes.length > 0) {
      params.set('productType', Array.from(new Set(productTypes)).join(','))
    } else {
      params.delete('productType')
    }

    params.set('page', '1')
    router.push(`/search?${params.toString()}`)
  }

  const removeFilter = (valueToRemove: string) => {
    updateFilters(selectedValues.filter(value => value !== valueToRemove))
  }

  return (
    <div className="mb-3 bg-gray-100 px-2 pb-2 pt-0">
      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <div className="w-full min-w-0 md:w-56">
          <label htmlFor="filters" className="sr-only">
            Filters
          </label>
          <Listbox value={selectedValues} onChange={updateFilters} multiple>
            <div className="relative">
              <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left text-base text-gray-700 shadow-xs ring-1 ring-inset ring-gray-300 focus:outline-hidden focus:ring-2 focus:ring-vi-gray sm:text-sm hover:text-gray-900 hover:cursor-pointer">
                <span className="block truncate">
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center ml-2 rounded-md bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                      {activeFilterCount}
                    </span>
                  )}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <AdjustmentsHorizontalIcon
                    aria-hidden="true"
                    className="size-5 text-gray-400"
                  />
                </span>
              </ListboxButton>

              <ListboxOptions
                transition
                className="absolute z-10 mt-1 max-h-96 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden data-closed:data-leave:opacity-0 data-leave:transition data-leave:duration-100 data-leave:ease-in sm:text-sm md:w-72"
              >
                <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Availability
                </div>
                {FILTER_OPTIONS.map(filter => (
                  <ListboxOption
                    key={filter.id}
                    value={`${FILTER_PREFIX}${filter.id}`}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-focus:bg-vi-red data-focus:cursor-pointer data-focus:text-white"
                  >
                    <span className="block truncate font-normal group-data-selected:font-semibold">
                      {filter.name}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-vi-red group-data-focus:text-white [.group:not([data-selected])_&]:hidden">
                      <CheckIcon aria-hidden="true" className="size-5" />
                    </span>
                  </ListboxOption>
                ))}

                <div className="mt-1 border-t border-gray-100 px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Product type
                </div>
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Loading types...
                  </div>
                ) : productTypeOptions.length > 0 ? (
                  productTypeOptions.map(option => (
                    <ListboxOption
                      key={option.value}
                      value={`${PRODUCT_TYPE_PREFIX}${option.value}`}
                      className="group relative cursor-default select-none py-2 pl-3 pr-14 text-gray-900 data-focus:bg-vi-red data-focus:cursor-pointer data-focus:text-white"
                    >
                      <span className="block truncate font-normal group-data-selected:font-semibold">
                        {formatProductTypeLabel(option.value)}
                      </span>
                      {option.count != null && (
                        <span className="absolute inset-y-0 right-8 flex items-center text-xs text-gray-500 group-data-focus:text-white">
                          {option.count}
                        </span>
                      )}
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-vi-red group-data-focus:text-white [.group:not([data-selected])_&]:hidden">
                        <CheckIcon aria-hidden="true" className="size-5" />
                      </span>
                    </ListboxOption>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No matching types
                  </div>
                )}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>

        {activeFilterCount > 0 && (
          <div className="flex min-w-0 flex-1 flex-wrap gap-2 md:pt-0.5">
            {initialFilters.map(filter => {
              const option = FILTER_OPTIONS.find(item => item.id === filter)
              if (!option) return null
              return (
                <FilterChip
                  key={filter}
                  label={option.name}
                  onRemove={() => removeFilter(`${FILTER_PREFIX}${filter}`)}
                />
              )
            })}
            {initialProductTypes.map(productType => (
              <FilterChip
                key={productType}
                label={formatProductTypeLabel(productType)}
                onRemove={() =>
                  removeFilter(`${PRODUCT_TYPE_PREFIX}${productType}`)
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const FilterChip = ({ label, onRemove }: FilterChipProps) => {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-yellow-100 px-2.5 py-1.5 text-sm font-medium text-yellow-800">
      <span className="truncate">{label}</span>
      <button
        type="button"
        className="rounded-xs p-0.5 text-yellow-900/60 hover:bg-yellow-200 hover:text-yellow-950 focus:outline-hidden focus:ring-2 focus:ring-vi-gray"
        onClick={onRemove}
      >
        <span className="sr-only">Remove {label}</span>
        <XMarkIcon aria-hidden="true" className="size-4" />
      </button>
    </span>
  )
}

const getProductTypeOptions = (
  facets: SearchFacet[],
  selectedProductTypes: string[]
) => {
  const productTypeFacet = facets.find(facet => facet.field === 'product_type')
  const facetOptions =
    productTypeFacet?.counts
      .filter(option => option.value && option.value !== 'article')
      .map(option => ({
        count: option.count,
        value: option.value,
      })) || []
  const selectedOptions = selectedProductTypes.map(value => ({
    count: facetOptions.find(option => option.value === value)?.count,
    value,
  }))

  return [
    ...facetOptions,
    ...selectedOptions.filter(
      selected => !facetOptions.some(option => option.value === selected.value)
    ),
  ]
}

const formatProductTypeLabel = (productType: string): string => {
  return productType
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      const lowerWord = word.toLowerCase()
      if (lowerWord === 'dvd') return 'DVD'
      if (lowerWord === 'dvds') return 'DVDs'
      return `${lowerWord.charAt(0).toUpperCase()}${lowerWord.slice(1)}`
    })
    .join(' ')
}

interface FilterSelectorClientProps {
  facets: SearchFacet[]
  initialFilters: string[]
  initialProductTypes: string[]
  isLoading: boolean
  query: string
}

interface FilterChipProps {
  label: string
  onRemove: () => void
}
